import { apiClient } from "../shared/utils/apiClient";
import logger from "../shared/utils/logger";

/**
 * Casino Service
 * Handles casino-related API calls
 */
class CasinoService {
  constructor() {
    this.baseUrl = "/api/casino";
  }

  /**
   * Get user purchase status for bonus modal logic
   * @returns {Promise<Object>} Purchase status information
   */
  async getPurchaseStatus() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/purchase-status`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching purchase status", error);
      throw error;
    }
  }

  /**
   * Get user timer data for first-time purchase countdown timers
   * @returns {Promise<Object>} Timer data with signup and purchase timestamps
   */
  async getTimerData() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/timer-data`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching timer data", error);
      throw error;
    }
  }

  /**
   * Get weekly purchase history for offers with weekly limits
   * @returns {Promise<Object>} Weekly purchase history by offer ID
   */
  async getWeeklyPurchaseHistory() {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/weekly-purchase-history`,
      );
      return response.data;
    } catch (error) {
      logger.error(
        "CasinoService: Error fetching weekly purchase history",
        error,
      );
      throw error;
    }
  }

  /**
   * Get offers for the casino
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of offers to fetch (optional - if not specified, returns all)
   * @param {boolean} options.featured - Whether to fetch only featured offers (default: false)
   * @param {string} options.filterType - Filter by offer type (e.g., 'first_time', 'second_time')
   * @returns {Promise<Object>} API response with offers
   */
  async getOffers(options = {}) {
    const { limit, featured = false, filterType } = options;

    const params = new URLSearchParams({
      featured: featured.toString(),
    });

    // Only add limit if specified
    if (limit !== undefined && limit !== null) {
      params.append("limit", limit.toString());
    }

    // Add filterType if specified
    if (filterType) {
      params.append("filterType", filterType);
    }

    try {
      const response = await apiClient.get(`${this.baseUrl}/offers?${params}`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching offers", error);
      throw new Error("Failed to fetch offers");
    }
  }

  /**
   * Get a specific offer by ID
   * @param {string} offerId - The offer ID
   * @returns {Promise<Object>} API response with offer details
   */
  async getOffer(offerId) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/offers/${offerId}`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching offer", error);
      throw new Error("Failed to fetch offer");
    }
  }

  /**
   * Get player balance
   * @param {string} playerId - The player ID
   * @returns {Promise<Object>} API response with balance data
   */
  async getPlayerBalance(playerId) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/balance/${playerId}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching player balance", error);
      throw new Error("Failed to fetch player balance");
    }
  }

  /**
   * Update player account type
   * @param {string} playerId - The player ID
   * @param {string} accountType - The account type ('crypto' or 'fiat')
   * @returns {Promise<Object>} API response
   */
  async updateAccountType(playerId, accountType) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/account-type`, {
        playerId,
        accountType,
      });
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error updating account type", error);
      throw new Error("Failed to update account type");
    }
  }

  /**
   * Get games catalog
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response with games data
   */
  async getGames(options = {}) {
    const params = new URLSearchParams();

    // Add parameters if provided
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/catalog?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching games catalog", error);
      throw new Error("Failed to fetch games catalog");
    }
  }

  /**
   * Get games for a specific category with pagination and search
   * @param {string} category - Game category
   * @param {Object} options - Query options with pagination and search
   * @param {number} options.limit - Number of games to fetch (default: 20)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @param {string} options.search - Search query for game name or provider
   * @returns {Promise<Object>} API response with games and pagination data
   */
  async getGamesByCategory(category, options = {}) {
    const params = new URLSearchParams();

    // Add parameters if provided
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          // Handle arrays (like providers, subcategories)
          if (value.length > 0) {
            params.append(key, value.join(","));
          }
        } else {
          params.append(key, value.toString());
        }
      }
    });

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/category/${category}?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching games by category", error);
      throw new Error("Failed to fetch games by category");
    }
  }

  /**
   * Get available filters for a category
   * @param {string} category - Game category
   * @returns {Promise<Object>} API response with filter options
   */
  async getCategoryFilters(category) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/category/${category}/filters`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching category filters", error);
      throw new Error("Failed to fetch category filters");
    }
  }

  /**
   * Get games by provider
   * @param {string} providerCode - The provider code
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of games to fetch (default: 20)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @param {string} options.search - Search query for game name
   * @returns {Promise<Object>} API response with games and pagination data
   */
  async getGamesByProvider(providerCode, options = {}) {
    const params = new URLSearchParams();

    // Add parameters if provided
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });

    try {
      const url = `${this.baseUrl}/games/provider/${providerCode}${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching games by provider", error);
      throw new Error("Failed to fetch games by provider");
    }
  }

  /**
   * Get available filters for a provider
   * @param {string} providerCode
   * @returns {Promise<Object>} API response with filter options
   */
  async getProviderFilters(providerCode) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/provider/${providerCode}/filters`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching provider filters", error);
      throw new Error("Failed to fetch provider filters");
    }
  }
  /**
   * Get game categories with counts
   * @returns {Promise<Array>} Array of categories with { key, name, count }
   */
  async getGameCategories() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/games/categories`);
      return response.data?.categories || [];
    } catch (error) {
      logger.error("CasinoService: Error fetching game categories", error);
      throw new Error("Failed to fetch game categories");
    }
  }

  /**
   * Get total enabled games count (lightweight)
   * @returns {Promise<number>} enabled games count
   */
  async getEnabledGamesCount() {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/stats/enabled`,
      );
      return typeof response.data?.enabled === "number"
        ? response.data.enabled
        : 0;
    } catch (error) {
      logger.error("CasinoService: Error fetching enabled games count", error);
      return 0;
    }
  }

  /**
   * Get total enabled providers count (lightweight)
   * @returns {Promise<number>} enabled providers count
   */
  async getEnabledProvidersCount() {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/providers/stats/enabled`,
      );
      return typeof response.data?.enabled === "number"
        ? response.data.enabled
        : 0;
    } catch (error) {
      logger.error(
        "CasinoService: Error fetching enabled providers count",
        error,
      );
      return 0;
    }
  }

  /**
   * Get all providers
   * @returns {Promise<Object>} API response with providers list
   */
  async getProviders() {
    try {
      // Use enriched providers endpoint (includes page content/stats)
      const response = await apiClient.get(`${this.baseUrl}/providers`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching providers", error);
      throw new Error("Failed to fetch providers");
    }
  }

  /**
   * Get game details by code
   * @param {string} gameCode - The game code
   * @returns {Promise<Object>} API response with game details
   */
  async getGameDetails(gameCode) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/games/${gameCode}`);
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching game details", error);
      throw new Error("Failed to fetch game details");
    }
  }

  /**
   * Get popular games
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response with popular games data
   */
  async getPopularGames(options = {}) {
    const params = new URLSearchParams();

    // Add parameters if provided
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/popular?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching popular games", error);
      throw new Error("Failed to fetch popular games");
    }
  }

  /**
   * Get new games
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response with new games data
   */
  async getNewGames(options = {}) {
    const params = new URLSearchParams();

    // Add parameters if provided
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/new?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error fetching new games", error);
      throw new Error("Failed to fetch new games");
    }
  }

  /**
   * Get general lobby recommendations (not user-specific)
   * Uses SS1 currency for Hub88 per platform rules
   * @param {Object} options
   * @param {number} options.limit - Number of items (default 25)
   * @param {string} options.currency - Currency code (default 'SS1')
   * @param {string} options.device - 'mobile' | 'desktop' (default 'mobile')
   * @param {string} options.locale - Locale code (default 'en')
   * @returns {Promise<Object>} API response with { recommendations, total, metadata }
   */
  async getGeneralRecommendations(options = {}) {
    const {
      limit = 25,
      currency = "SS1",
      device = "mobile",
      locale = "en",
    } = options;

    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    params.append("currency", currency);
    params.append("device", device);
    params.append("locale", locale);

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/recommendations/general?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error(
        "CasinoService: Error fetching general recommendations",
        error,
      );
      throw new Error("Failed to fetch recommendations");
    }
  }

  /**
   * Search games by name or provider
   * @param {string} searchTerm - The search term
   * @param {Object} options - Search options
   * @param {string} options.category - Filter by category (optional)
   * @param {number} options.limit - Number of results to return (default: 50)
   * @returns {Promise<Object>} API response with search results
   */
  async searchGames(searchTerm, options = {}) {
    const params = new URLSearchParams({
      q: searchTerm,
    });

    // Add optional parameters
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    try {
      const response = await apiClient.get(
        `${this.baseUrl}/games/search?${params}`,
      );
      return response.data;
    } catch (error) {
      logger.error("CasinoService: Error searching games", error);
      throw new Error("Failed to search games");
    }
  }

  /**
   * Launch a game
   * @param {string} gameCode - The game code
   * @param {Object} options - Launch options
   * @returns {Promise<Object>} API response with game launch data
   */
  async launchGame(gameCode, options = {}) {
    const startTime = Date.now();
    const requestId = `frontend_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    try {
      logger.info(`[${requestId}] CasinoService: Starting game launch`, {
        requestId,
        gameCode,
        options,
        timestamp: new Date().toISOString(),
      });

      const beforeRequest = Date.now();
      const response = await apiClient.post(
        `${this.baseUrl}/games/${gameCode}/launch`,
        options,
      );
      const requestDuration = Date.now() - beforeRequest;
      const totalDuration = Date.now() - startTime;

      logger.info(`[${requestId}] CasinoService: Game launch successful`, {
        requestId,
        gameCode,
        requestDuration: `${requestDuration}ms`,
        totalDuration: `${totalDuration}ms`,
        hasGameUrl: !!response.data?.gameUrl,
      });

      return response.data;
    } catch (error) {
      const totalDuration = Date.now() - startTime;

      logger.error(`[${requestId}] CasinoService: Error launching game`, {
        requestId,
        gameCode,
        error: error.message,
        errorCode: error.code,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        totalDuration: `${totalDuration}ms`,
        isTimeout: error.code === "ECONNABORTED",
        timestamp: new Date().toISOString(),
      });

      throw new Error("Failed to launch game");
    }
  }
}

// Export singleton instance
export const casinoService = new CasinoService();
export default casinoService;
